(() => {
  if (customElements.get('product-compare')) return;

  class ProductCompare extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.maxItems = Number(this.dataset.maxItems || 5);
      this.storageKey = this.dataset.storageKey || 'goomiProductCompare';
      this.modeStorageKey = `${this.storageKey}:mode`;
      this.toggle = this.querySelector('[data-compare-toggle]');
      this.panel = this.querySelector('[data-compare-panel]');
      this.slots = this.querySelector('[data-compare-slots]');
      this.countEl = this.querySelector('[data-compare-count]');
      this.openButton = this.querySelector('[data-compare-open]');
      this.clearButton = this.querySelector('[data-compare-clear]');
      this.modal = this.querySelector('#product-compare-modal');
      this.modalContent = this.querySelector('[data-compare-modal-content]');
      this.items = this.readStorage();
      this.restoreCompareMode();

      this.addEventListener('change', this.handleChange);
      this.addEventListener('click', this.handleClick);
      document.addEventListener('on:facet-filters:updated', this.handleDomUpdate);

      this.observer = new MutationObserver(this.handleDomUpdate);
      this.observer.observe(this, { childList: true, subtree: true });

      this.render();

      // Prune stored items whose product is no longer available on the storefront
      // (e.g. set to draft/unpublished or deleted) — the localStorage snapshot persists
      // but its product page now returns 404. Runs async so it never blocks initial render.
      this.validateItems();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.handleChange);
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('on:facet-filters:updated', this.handleDomUpdate);
      if (this.observer) this.observer.disconnect();
    }

    handleChange = (event) => {
      if (event.target.matches('[data-compare-toggle]')) {
        this.classList.toggle('is-compare-active', event.target.checked);
        this.writeCompareMode(event.target.checked);
        this.render();
        return;
      }

      if (!event.target.matches('[data-compare-checkbox]')) return;

      const checkbox = event.target;
      const product = this.getCardData(checkbox.closest('[data-compare-card]'));
      if (!product) {
        checkbox.checked = false;
        return;
      }

      if (checkbox.checked) {
        if (this.items.length >= this.maxItems && !this.hasItem(product.id)) {
          checkbox.checked = false;
          return;
        }
        this.addItem(product);
      } else {
        this.removeItem(product.id);
      }
    };

    handleClick = (event) => {
      const removeButton = event.target.closest('[data-compare-remove]');
      if (removeButton) {
        event.preventDefault();
        this.removeItem(removeButton.dataset.compareRemove);
        return;
      }

      if (event.target.closest('[data-compare-clear]')) {
        event.preventDefault();
        this.clearItems();
        return;
      }

      if (event.target.closest('[data-compare-open]')) {
        event.preventDefault();
        if (this.items.length < 2) return;
        this.renderModal();
        if (this.modal && typeof this.modal.open === 'function') {
          this.modal.open(event.target.closest('[data-compare-open]'));
        } else if (this.modal) {
          this.modal.setAttribute('open', '');
        }
      }
    };

    handleDomUpdate = () => {
      window.requestAnimationFrame(() => this.syncCards());
    };

    readStorage() {
      try {
        const parsed = JSON.parse(window.localStorage.getItem(this.storageKey) || '[]');
        if (!Array.isArray(parsed)) return [];
        return parsed.filter((item) => item && item.id).slice(0, this.maxItems);
      } catch (error) {
        return [];
      }
    }

    writeStorage() {
      try {
        window.localStorage.setItem(this.storageKey, JSON.stringify(this.items));
      } catch (error) {
        // Ignore storage errors and keep the in-memory state for this page view.
      }
    }

    restoreCompareMode() {
      if (!this.toggle) return;
      try {
        this.toggle.checked = window.localStorage.getItem(this.modeStorageKey) === 'true';
      } catch (error) {
        this.toggle.checked = false;
      }
      this.classList.toggle('is-compare-active', this.toggle.checked);
    }

    writeCompareMode(active) {
      try {
        window.localStorage.setItem(this.modeStorageKey, active ? 'true' : 'false');
      } catch (error) {
        // Ignore storage errors and keep the visible state for this page view.
      }
    }

    getCardData(card) {
      const node = card?.querySelector('script[data-compare-product]');
      if (!node) return null;
      try {
        return JSON.parse(node.textContent.trim());
      } catch (error) {
        return null;
      }
    }

    /**
     * Removes stored items whose product page no longer resolves (draft, unpublished,
     * or deleted products return 404 on the storefront). Network errors are ignored so
     * items are never dropped on a flaky connection.
     */
    async validateItems() {
      if (!this.items.length) return;

      // Collect invalid ids from a snapshot so items added/removed while the
      // HEAD checks are in flight are never dropped or mismatched by index.
      const snapshot = this.items;
      const invalidIds = new Set();
      await Promise.all(
        snapshot.map(async (item) => {
          if (!item.url) return;
          try {
            const response = await fetch(item.url, { method: 'HEAD' });
            if (!response.ok) invalidIds.add(String(item.id));
          } catch (error) {
            // Ignore network errors so items are never dropped on a flaky connection.
          }
        })
      );

      if (invalidIds.size === 0) return;
      const validItems = this.items.filter((item) => !invalidIds.has(String(item.id)));
      if (validItems.length !== this.items.length) {
        this.items = validItems;
        this.persistAndRender();
      }
    }

    hasItem(id) {
      return this.items.some((item) => String(item.id) === String(id));
    }

    addItem(product) {
      if (!this.hasItem(product.id)) {
        this.items = [...this.items, product].slice(0, this.maxItems);
      } else {
        this.items = this.items.map((item) => (String(item.id) === String(product.id) ? product : item));
      }
      this.persistAndRender();
    }

    removeItem(id) {
      this.items = this.items.filter((item) => String(item.id) !== String(id));
      this.persistAndRender();
    }

    clearItems() {
      this.items = [];
      this.persistAndRender();
    }

    persistAndRender() {
      this.writeStorage();
      this.render();
      if (this.modal?.hasAttribute('open')) {
        if (this.items.length > 0) {
          this.renderModal();
        } else if (typeof this.modal.close === 'function') {
          this.modal.close();
        } else {
          this.modal.removeAttribute('open');
        }
      }
    }

    render() {
      this.syncCards();
      this.renderPanel();
    }

    syncCards() {
      const selectedIds = this.items.map((item) => String(item.id));
      const atLimit = this.items.length >= this.maxItems;
      this.querySelectorAll('[data-compare-checkbox]').forEach((checkbox) => {
        const isSelected = selectedIds.includes(String(checkbox.value));
        checkbox.checked = isSelected;
        checkbox.disabled = !isSelected && atLimit;
      });
    }

    renderPanel() {
      const active = !!this.toggle?.checked;
      const hasItems = this.items.length > 0;

      if (this.panel) {
        this.panel.classList.toggle('is-active', active && hasItems);
        this.panel.setAttribute('aria-hidden', active && hasItems ? 'false' : 'true');
      }

      if (this.countEl) {
        this.countEl.textContent = this.formatCount();
      }

      if (this.openButton) {
        this.openButton.disabled = this.items.length < 2;
      }

      if (!this.slots) return;
      this.slots.innerHTML = '';

      for (let index = 0; index < this.maxItems; index += 1) {
        const item = this.items[index];
        const slot = document.createElement('div');
        slot.className = item ? 'product-compare-panel__item' : 'product-compare-panel__slot';

        if (item) {
          slot.innerHTML = `
            ${this.renderImage(item, 'product-compare-panel__image img-fit')}
            <button type="button" class="product-compare__remove" data-compare-remove="${this.escapeAttribute(item.id)}" aria-label="${this.escapeAttribute(this.labels.remove)}">
              <span class="visually-hidden">${this.escapeHtml(this.labels.remove)}</span>
            </button>
          `;
        } else {
          slot.innerHTML = `<span class="visually-hidden">${this.escapeHtml(this.labels.emptySlot)}</span>`;
        }

        this.slots.append(slot);
      }
    }

    renderModal() {
      if (!this.modalContent) return;
      const columns = Math.max(this.items.length, 1);

      const productCells = this.items.map((item) => `
        <div class="product-compare-table__cell product-compare-table__product">
          <div class="product-compare-table__image-wrapper relative">
            <div class="product-compare-table__image-wrap relative">
              ${this.renderImage(item, 'product-compare-table__image img-fit')}
            </div>
            <button type="button" class="product-compare__remove" data-compare-remove="${this.escapeAttribute(item.id)}" aria-label="${this.escapeAttribute(this.labels.remove)}">
              <span class="visually-hidden">${this.escapeHtml(this.labels.remove)}</span>
            </button>
          </div>
          <p class="product-compare-table__title">${this.escapeHtml(item.title)}</p>
          <a class="btn btn--primary" href="${this.escapeAttribute(item.url)}">${this.escapeHtml(this.labels.viewDetails)}</a>
        </div>
      `).join('');

      const descriptionCells = this.items.map((item) => `
        <div class="product-compare-table__cell product-compare-table__text">${this.escapeHtml(item.description || '')}</div>
      `).join('');

      const vendorCells = this.items.map((item) => `
        <div class="product-compare-table__cell product-compare-table__text">${this.escapeHtml(item.vendor || '')}</div>
      `).join('');

      const priceCells = this.items.map((item) => `
        <div class="product-compare-table__cell product-compare-table__text">${this.escapeHtml(item.price || '')}</div>
      `).join('');

      this.modalContent.innerHTML = `
        <div class="product-compare-table" style="--compare-columns: ${columns};">
          <div class="product-compare-table__cell product-compare-table__label"></div>
          ${productCells}
          <div class="product-compare-table__cell product-compare-table__label">${this.escapeHtml(this.labels.description)}</div>
          ${descriptionCells}
          <div class="product-compare-table__cell product-compare-table__label">${this.escapeHtml(this.labels.vendor)}</div>
          ${vendorCells}
          <div class="product-compare-table__cell product-compare-table__label">${this.escapeHtml(this.labels.price)}</div>
          ${priceCells}
        </div>
      `;
    }

    renderImage(item, className) {
      if (!item.image) return `<div class="${className}"></div>`;
      return `<img class="${className}" src="${this.escapeAttribute(item.image)}" alt="${this.escapeAttribute(item.imageAlt || item.title || '')}" loading="lazy">`;
    }

    formatCount() {
      return this.labels.selectedCount
        .replace('[count]', String(this.items.length))
        .replace('[max]', String(this.maxItems));
    }

    escapeHtml(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    escapeAttribute(value) {
      return this.escapeHtml(value);
    }

    get labels() {
      return {
        compare: this.dataset.labelCompare || 'Compare',
        selectedCount: this.dataset.labelSelectedCount || 'Compare ([count]/[max])',
        clearAll: this.dataset.labelClearAll || 'Clear All',
        remove: this.dataset.labelRemove || 'Remove',
        viewDetails: this.dataset.labelViewDetails || 'View Details',
        description: this.dataset.labelDescription || 'Description',
        vendor: this.dataset.labelVendor || 'Vendor',
        price: this.dataset.labelPrice || 'Price',
        emptySlot: this.dataset.labelEmptySlot || 'Empty compare slot',
      };
    }
  }

  customElements.define('product-compare', ProductCompare);
})();
